import { createApi } from '@reduxjs/toolkit/query/react';

import { setAppNotice } from '@/app/store/appShellSlice';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import type { ApiResponseBase } from '@/services/types/api';

const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

export type ReceptionCommitPartRequest = {
  sku: string;
  name?: string;
  quantity: number;
  unit_price: number;
};

export type ReceptionCommitPaymentRequest = {
  amount: number;
  payment_method_id?: number;
  reference?: string | null;
  notes?: string | null;
};

export type ReceptionCommitDeviceRequest = {
  client_device_id: string;
  equipment_type_id: number;
  equipment_brand_id: number;
  equipment_model_id: number;
  imei_serie: string;
  diagnostic: string;
  reported_failure: string;
  has_lock?: boolean;
  lock_type?: number | null;
  lock_info?: string | null;
  warranty_id?: number | null;
  repair_cost: number;
  parts?: ReceptionCommitPartRequest[];
  payment?: ReceptionCommitPaymentRequest | null;
};

export type ReceptionCommitRequest = {
  client: {
    name: string;
    phone_number?: string;
    contact_number?: string;
  };
  devices: ReceptionCommitDeviceRequest[];
};

export type ReceptionCommitOrderPayment = {
  id: number;
  amount: number;
  is_deposit: boolean;
};

export type ReceptionCommitOrder = {
  client_device_id: string;
  id: number;
  order_code?: string | null;
  intake_id?: number | null;
  intake_code?: string | null;
  total_cost: number;
  paid_amount: number;
  payment?: ReceptionCommitOrderPayment | null;
};

export type ReceptionCommitResponseData = {
  workflow_id: number;
  workflow_status: string;
  client: {
    id: number;
    created: boolean;
  };
  orders: ReceptionCommitOrder[];
};

export const receptionsApi = createApi({
  reducerPath: 'receptionsApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['ReceptionCommit'],
  endpoints: (builder) => ({
    commitReception: builder.mutation<
      ApiResponseBase<ReceptionCommitResponseData>,
      { body: ReceptionCommitRequest; idempotencyKey: string }
    >({
      query: ({ body, idempotencyKey }) => ({
        url: '/v1/receptions/commit',
        method: 'POST',
        body,
        headers: {
          'X-Idempotency-Key': idempotencyKey,
        },
      }),
      transformErrorResponse: handlerErrorResponse('Receptions.Commit'),
      invalidatesTags: ['ReceptionCommit'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar la recepción',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Receptions.Commit'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'receptions.commit',
              endpointName: e?.endpointName || 'Receptions.Commit',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
  }),
});
